import {
	Component,
	OnInit,
	Input,
	OnDestroy,
	ViewChild,
	AfterViewInit,
	ContentChild,
	TemplateRef,
	AfterContentInit,
	ViewEncapsulation,
	ChangeDetectorRef,
	EventEmitter,
	Output,
	ViewChildren,
	QueryList,
	SimpleChanges
} from '@angular/core';
import { IdService } from '../../shared/services/id.service';
import { Column, Locale, Row, DataTypes } from './datatable.models';
import { formatNumber } from '../../shared/utils/formatting';
import { currencyOpts } from '../../shared/constants/common';
import { sortTypes } from '../../shared/types/common';
import { DatatableService } from './datatable.service';
import { DatatableGlobalSearcherComponent } from './datatable-global-searcher/datatable-global-searcher.component';
import { IDatatableGlobalSearchEvent } from './datatable-global-searcher/datatable-global-searcher.type';
import { DatatableColumnFilterComponent } from './datatable-column-filter/datatable-column-filter.component';
import { DatatableAdvancedSearcherComponent } from './datatable-advanced-searcher/datatable-advanced-searcher.component';
import { IDatatableAdvancedSearchEvent } from './datatable-advanced-searcher/datatable-advanced-seacher.type';
import { DatatablePaginatorComponent } from './datatable-paginator/datatable-paginator.component';
import { IDatatablePaginatorEvent, IDatatablePageSizeEvent } from './datatable-paginator/datatable-paginator.types';
import { ICheckboxUpdateEvent } from '../checkbox/checkbox.types';
import { IDatatableRowSelectedEvent, IDatatableSortEvent } from './datatable.types';
import { IGenericSelectOption } from '../../shared/types/forms';

@Component({
	selector: 'rbc-datatable',
	templateUrl: './datatable.component.html',
	styleUrls: ['./datatable.component.scss'],
	encapsulation: ViewEncapsulation.None,
	providers: [DatatableService]
})
export class DatatableComponent implements OnInit, OnDestroy, AfterViewInit, AfterContentInit {
	@Input() id: string;

	@Input('advanced-search') advancedSearch: boolean = false;
	@Input('column-fixed') columnFixed: boolean = false;
	@Input() columns: Column[];
	@Input('columns-group') columnsGroup: boolean = false;
	@Input() compact: boolean = false;
	@Input() copy: boolean = false;
	@Input('customizable-columns') customizableColumns: boolean = false;
	@Input('customizable-columns-display') customizableColumnsDisplay: boolean = true;
	@Input('download-json') downloadJson: boolean = false;
	@Input('download-csv') downloadCsv: boolean = false;
	@Input() editable: boolean = false;
	@Input('expandable-row') expandableRow: boolean = false;
	@Input('expandable-row-arrow') expandableRowArrow: boolean = true;
	@Input('expandable-row-content-validator') expandableRowContentValidator: (...any) => boolean;
	@Input('global-search') globalSearch: boolean = false;
	@Input('header-fixed') headerFixed: boolean = false;
	@Input('max-table-body-height') maxTableBodyHeight: number = 320;
	@Input() locale = new Locale();
	@Input() pagination: boolean = false;
	@Input('pagination-display') paginationDisplay: number[];
	@Input('pagination-display-preselected') set paginationDisplayPreselected(pageSize: string) {
		this._currentPageSize = `${pageSize || 10}`;
	}
	@Input('responsive-columns') responsiveColumns: boolean = false;
	@Input() rows: Row[];
	@Input('selectable-rows') selectableRows: boolean = false;
	@Input() sortable: boolean = false;
	@Input('prevent-sort') preventSort: boolean = false;
	@Input() title: string;

	@Output('onallrowsselected') onAllRowsSelected = new EventEmitter<Row[]>();
	@Output('onallrowsdeselected') onAllRowsDeselected = new EventEmitter();
	@Output('oneditallbuttonclicked') onEditAllButtonClicked = new EventEmitter();
	@Output('onsaveallbuttonclicked') onSaveAllButtonClicked = new EventEmitter();
	@Output('oneditrowbuttonclicked') onEditRowButtonClicked = new EventEmitter<Row>();
	@Output('onsaverowbuttonclicked') onSaveRowButtonClicked = new EventEmitter<Row>();
	@Output('onrowexpanded') onRowExpanded = new EventEmitter<Row>();
	@Output('onrowdeexpanded') onRowDeexpanded = new EventEmitter<Row>();
	@Output('onrowselected') onRowSelected = new EventEmitter<IDatatableRowSelectedEvent>();
	@Output('onrowdeselected') onRowDeselected = new EventEmitter<Row>();
	@Output('onpaginatorbuttonclicked') onPaginatorButtonClicked = new EventEmitter<IDatatablePaginatorEvent>();
	@Output('onpagesizeselectchanged') onPageSizeSelectChanged = new EventEmitter<IDatatablePageSizeEvent>();
	@Output('oncolumnschanged') onColumnsChanged = new EventEmitter<Column[]>();
	@Output('onsortchanged') onSortChanged = new EventEmitter<IDatatableSortEvent>();
	@Output('onglobalsearchfocusout') onGlobalSearchFocusout = new EventEmitter<IDatatableGlobalSearchEvent>();
	@Output('onglobalsearchtermupdate') onGlobalSearchTermUpdate = new EventEmitter<IDatatableGlobalSearchEvent>();

	@ViewChild(DatatableGlobalSearcherComponent) globalSearcherComponent: DatatableGlobalSearcherComponent;
	@ViewChild(DatatableColumnFilterComponent) columnFilterComponent: DatatableColumnFilterComponent;
	@ViewChildren(DatatableAdvancedSearcherComponent) advancedSearcherComponents: QueryList<
		DatatableAdvancedSearcherComponent
	>;
	@ViewChild(DatatablePaginatorComponent) paginatorComponent: DatatablePaginatorComponent;

	DataTypes = DataTypes; // reference for use in template
	selectAll: boolean = false;
	editingAll = false;
	rowEditing = false;
	editingRow;
	selectAllLabel = 'Select or deselect all rows';
	sortLabel = 'Sort';
	sortUpLabel = 'Sort up';
	sortDownLabel = 'Sort down';
	expandRowLabel = 'Expand row';
	expandedRows: Row[] = [];
	buttonCloseTxt = 'Ok';
	sortedColumn: Column;
	sortedColumnIndex = 0;
	sortType: sortTypes;
	filteredRows: Row[] = [];
	selectedRows: Row[] = [];
	currentPage = 1;
	isGlobalSearchable: boolean;
	globalSearchTerm = '';
	advancedSearchObjects: IDatatableAdvancedSearchEvent[] = [];
	loading = false;
	copyFeedbackActive = false;
	mainColumnPresent = false;
	tBodyWidth = '';
	isIE: boolean = !!window.hasOwnProperty('MSInputMethodContext') && !!document.hasOwnProperty('documentmode');
	showDefaultRow = false;

	private _currentPageSize;

	@ContentChild('rowTemplate', { read: TemplateRef }) rowUserTemplate: TemplateRef<any>;
	@ContentChild('expandableRowTemplate', { read: TemplateRef }) expandRowUserTemplate: TemplateRef<any>;
	@ContentChild('paginationTemplate', { read: TemplateRef }) paginationUserTemplate: TemplateRef<any>;
	@ContentChild('rowCellTemplate', { read: TemplateRef }) rowCellUserTemplate: TemplateRef<any>;
	@ContentChild('rowCellFixedTemplate', { read: TemplateRef }) rowCellFixedUserTemplate: TemplateRef<any>;

	get currentPageSize() {
		return this._currentPageSize || '10';
	}

	set currentPageSize(pageSize: number) {
		this._currentPageSize = pageSize;
	}

	get displayedRowsOnPage(): Row[] {
		return this.pagination ? this.paginatedRows : this.filteredRows;
	}

	get totalPages(): number {
		if (this.filteredRows) {
			return Math.ceil(this.filteredRows.length / this.currentPageSize);
		}
	}

	get paginatedRows(): Row[] {
		// IMPROVE: why does this logic even exist here? it should be in the paginator component
		if (this.filteredRows) {
			return this.filteredRows.filter((row, index) => {
				const start = (this.currentPage - 1) * this.currentPageSize;
				const end = this.currentPage * this.currentPageSize;
				if (index >= start && index < end) {
					return true;
				}
			});
		}
	}

	get headerFixedStyle(): object {
		if (this.headerFixed) {
			const height = this.maxTableBodyHeight ? this.maxTableBodyHeight : 400;

			return {
				height: `${height}px`
			};
		}
	}

	constructor(
		private idService: IdService,
		private changeDetector: ChangeDetectorRef,
		private datatableService: DatatableService
	) {}

	// NOTE: all commented out code from Vue counterpart was left out.
	ngOnInit() {
		if (!this.id) {
			this.id = this.idService.generate();
		}
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes) {
			if (changes.columns) {
				this.mainColumnPresent = changes.columns.currentValue
					? changes.columns.currentValue.filter(col => col.main).length > 0
					: false;
			}

			if (changes.rows && changes.rows) {
				this.filteredRows = (changes.rows.currentValue || []).slice();
			}

			if (changes.responsiveColumns && changes.responsiveColumns.currentValue) {
				window.addEventListener('resize', this.onWindowSizeChanged);
			}

			if (changes.copy && changes.copy.currentValue) {
				const table = document.getElementById(this.id);
				if (table) {
					table.addEventListener('copy', e => this.overwriteClipboardData(e));
				}
			}
		}
	}

	ngAfterViewInit(): void {}

	ngAfterContentInit(): void {}

	ngOnDestroy(): void {
		if (this.idService) {
			this.idService.remove(this.id);
		}

		// remove listeners
		window.removeEventListener('resize', this.onWindowSizeChanged);
	}

	allRowsSelectedUpdate(event: ICheckboxUpdateEvent) {
		if (event && event.checked) {
			this.selectedRows = [];
			if (this.pagination) {
				// If pagination enabled, select current displayed rows on page.
				this.displayedRowsOnPage.forEach(r => {
					this.selectedRows.push(r);
				});
			} else {
				// if pagination disabled, select filtered rows
				this.filteredRows.forEach(r => {
					this.selectedRows.push(r);
				});
			}

			this.displayedRowsOnPage.forEach(row => (row.checked = true));

			// emit to observers
			this.onAllRowsSelected.emit(this.selectedRows);
		} else {
			// if checkbox unchecked, remove all from the selectedRows array
			this.selectedRows = [];
			this.displayedRowsOnPage.forEach(row => (row.checked = false));

			// emit to observers
			this.onAllRowsDeselected.emit();
		}
	}

	rowSelected(event, index: number, row: Row) {
		if (this.displayedRowsOnPage[index].checked) {
			// add selected row to the selectedRows array
			this.selectedRows.push(row);

			// if all rows selected, then check the selectAllRows checkbox
			if (this.pagination) {
				if (this.selectedRows.length === this.paginatedRows.length) {
					this.selectAll = true;
				}
			} else {
				if (this.selectedRows.length === this.filteredRows.length) {
					this.selectAll = true;
				}
			}

			this.onRowSelected.emit({ currentSelectedRow: row, allSelectedRows: this.selectedRows });
		} else {
			// Remove selected row from selectedRows array
			this.selectedRows = this.selectedRows.filter(sRow => sRow !== row);
			// Uncheck the select all checkbox
			this.selectAll = false;

			this.onRowDeselected.emit(row);
		}
	}
	isExpandedRowValid(row): boolean {
		return this.expandableRowContentValidator ? this.expandableRowContentValidator(row) : true;
	}
	isRowExpanded(row: Row): boolean {
		// Check if current row is in the expandedRows array
		return this.expandedRows.indexOf(row) > -1;
	}

	onExpandArrowClicked(row: Row, index: number) {
		// If current row expanded, then fold the current row
		// else Set the expandedRow as current row
		if (this.isRowExpanded(row)) {
			this.expandedRows = this.expandedRows.filter(r => r !== row);
			this.onRowDeexpanded.emit(row);
		} else {
			this.expandedRows.push(row);
			this.onRowExpanded.emit(row);
		}
	}

	sort(col: Column, colIndex: number) {
		// If current table is not sortable , simply return.
		if (!this.sortable) {
			return;
		}

		// If current column is not sortable, simply return.
		if (!this.isSortableColumn(colIndex)) {
			return;
		}

		// Set current softed column and index
		this.sortedColumn = col;
		this.sortedColumnIndex = colIndex;

		// Flip sort type if resort current column, otherwise sort by asc.
		if (this.sortedColumnIndex === colIndex) {
			this.sortType = this.sortType === 'asc' ? 'desc' : 'asc';
		} else {
			this.sortType = 'asc';
			this.sortedColumnIndex = colIndex;
		}

		if (!this.preventSort) {
			// If we want to prevent default sort
			// Sort the filtered rows by column index and sort type
			this.sortFilteredRows(this.sortedColumn, this.sortedColumnIndex, this.sortType);
		}

		this.onSortChanged.emit({
			sortType: this.sortType,
			sortedColumnIndex: this.sortedColumnIndex
		});
	}

	getCellDisplayValueByColumnIndex(row: Row, colIndex: number, col: Column) {
		return this.getCellDisplayValue(row.cells[colIndex], col);
	}

	getCellDisplayValue(value: any, col: Column): string {
		// NOTE: will evaluate empty strings as false.
		if (value && col) {
			switch (col.dataType) {
				case 'number':
					const displayNumber = col.formatNumber
						? formatNumber(
								this.datatableService.assertCellType(value)
									? this.datatableService
											.assertCellType(value)
											.toString()
											.replace('-', '')
									: '',
								col.formatNumberLang || 'en',
								col.formatNumberDecimal || 2
						  )
						: this.datatableService.assertCellType(value)
						? this.datatableService
								.assertCellType(value)
								.toString()
								.replace('-', '')
						: '';
					let prefix = '';
					let suffix = '';
					if (col.colored) {
						if (this.datatableService.assertCellType(value) < 0) {
							prefix = '<span class="c-alert-red">';
							suffix = '</span>';
						} else if (this.datatableService.assertCellType(value) > 0) {
							prefix = '<span class="c-alert-green">';
							suffix = '</span>';
						}
					}
					const indicator =
						this.datatableService.assertCellType(value) === 0
							? ''
							: col.signed
							? this.datatableService.assertCellType(value) < 0
								? '-'
								: '+'
							: col.arrow
							? this.datatableService.assertCellType(value) < 0
								? '<span class="far fa-long-arrow-alt-down"></span> -'
								: '<span class="far fa-long-arrow-alt-up"></span> &nbsp;'
							: this.datatableService.assertCellType(value) < 0
							? '-'
							: '';
					if (col.dataUnit) {
						if (
							col.dataUnit === '%' ||
							col.dataUnit === ' %' ||
							(typeof col.dataUnitPosition !== 'undefined' && col.dataUnitPosition === 'right')
						) {
							return prefix + indicator + displayNumber + col.dataUnit + suffix;
						} else {
							return prefix + indicator + col.dataUnit + displayNumber + suffix;
						}
					} else {
						return prefix + indicator + displayNumber + suffix;
					}
					break;
				case 'select':
					if (value) {
						return this.datatableService.assertCellType(value)
							? this.datatableService.assertCellType(value).toUpperCase()
							: '';
					}
					break;
				case 'date':
					return new Date(this.datatableService.assertCellType(value)).toLocaleString(
						col.dataDateLocale || 'en-us',
						{
							...col.dataDateFormat,
							timeZone: 'UTC'
						}
					);
				case 'string':
					return this.datatableService.assertCellType(value);
			}

			// fall through
			return '-';
		}
	}

	getCellByColumnIndex(row: Row, colIndex: number) {
		return row.cells[colIndex];
	}

	onCellValueChanges(changeEvent: IGenericSelectOption | string, row: Row, col: Column, colIndex?: number) {
		// get current column index
		const ind = colIndex ? colIndex : this.columns.indexOf(col);
		// update original rows based on cell changes
		this.filteredRows.filter(r => r === row)[0].cells[ind] =
			col.dataType === 'select' ? (changeEvent as IGenericSelectOption).value : changeEvent;
	}

	selectOptions_currency(value: string) {
		return currencyOpts.map(opt => {
			opt.selected = opt.value === value.toLowerCase();
			return opt;
		});
	}

	getColumnByColumnIndex(colIndex: number) {
		return this.columns[colIndex];
	}

	columnsGroupColspan(cols: Column[]): number[] {
		const columnGroups = [];
		let colspan = 1;

		for (let index = 0; index < cols.length; index++) {
			if (cols[index].show) {
				if (typeof cols[index].headerGroup === 'undefined') {
					columnGroups[index] = 1;
				} else {
					if (typeof cols[index + 1] !== 'undefined') {
						if (cols[index + 1].headerGroup !== cols[index].headerGroup) {
							columnGroups[index] = colspan;
							colspan = 1;
						} else {
							columnGroups[index] = 0;
							colspan++;
						}
					} else {
						// last element
						if (cols[index - 1].headerGroup === cols[index].headerGroup) {
							columnGroups[index] = colspan;
						}
					}
				}
			}
		}
		return columnGroups;
	}

	isSortableColumn(colIndex: number) {
		const { sortable } = this.columns[colIndex];
		return sortable;
	}

	sortFilteredRows(col: Column, colIndex: number, sortType: sortTypes) {
		let modifier = 1;
		if (sortType === 'desc') {
			modifier = -1;
		}
		if (col.dataType) {
			if (col.dataType === 'number') {
				this.filteredRows = this.filteredRows.sort((a, b) => {
					if (
						this.datatableService.assertCellType(a.cells[colIndex]) -
							this.datatableService.assertCellType(b.cells[colIndex]) >
						0
					) {
						return 1 * modifier;
					}
					if (
						this.datatableService.assertCellType(a.cells[colIndex]) -
							this.datatableService.assertCellType(b.cells[colIndex]) <
						0
					) {
						return -1 * modifier;
					}
					return 0;
				});
			} else {
				this.filteredRows = this.filteredRows.sort((a, b) => {
					if (a.cells[colIndex] > b.cells[colIndex]) {
						return 1 * modifier;
					}
					if (a.cells[colIndex] < b.cells[colIndex]) {
						return -1 * modifier;
					}
					return 0;
				});
			}
		}
	}

	editAllButtonClickHandler(event) {
		// Fold expanded row if exist
		this.expandedRows = [];
		// Set current status as editing
		this.editingAll = true;

		this.onEditAllButtonClicked.emit();
	}

	saveAllButtonClickHandler(event) {
		// Set current status as editing=false
		this.editingAll = false;
		this.rowEditing = false;
		this.editingRow = null;

		this.changeDetector.detectChanges();

		this.onSaveAllButtonClicked.emit();
	}

	editRowButtonClickHandler(row: Row) {
		// Fold expanded row if exist
		this.expandedRows = [];
		// Set current status as editing
		this.rowEditing = true;
		// Set current editing row
		this.editingRow = row;

		this.onEditRowButtonClicked.emit(row);
	}

	saveRowButtonClickHandler(row: Row) {
		// Set current status as editing=false
		this.rowEditing = false;
		// Set current editing row to null
		this.editingRow = null;

		this.changeDetector.detectChanges();
		this.onSaveRowButtonClicked.emit(row);
	}

	emitCopyEvent(event) {
		// Create a fake textarea to capture the copy event and execute the clipboard overwrite.
		const el = document.createElement('textarea');
		el.addEventListener('copy', e => this.overwriteClipboardData(e));
		el.setAttribute('readonly', '');
		el.style.position = 'absolute';
		el.style.left = '-9999px';
		document.body.appendChild(el);
		el.select();
		document.execCommand('copy');
		document.body.removeChild(el);
	}

	generateCopy(): string {
		const extract = [];
		let extractString;
		const headers = [];
		this.columns.forEach(el => {
			if (el.show) {
				headers.push(`"${el.header.split('"').join('""')}"`);
			}
		});
		extract.push(headers.join('\t'));
		const rows = this.filteredRows.map((row, index) => {
			const tempRow = [];
			row.cells.forEach((val, cellIndex) => {
				if (this.columns[cellIndex].show) {
					tempRow.push(
						`"${String(val)
							.split('"')
							.join('""')}"`
					);
				}
			});
			const formattedRow = tempRow.join('\t');
			extract.push(formattedRow);
			return formattedRow;
		});
		extractString = extract.join('\n');
		return extractString;
	}

	generateJSON(event) {
		const extract = [];
		this.filteredRows.forEach((row, index) => {
			const tempRow = {};
			row.cells.forEach((cell, cellIndex) => {
				tempRow[this.columns[cellIndex].header] = cell;
			});
			extract.push(tempRow);
		});
		const blob = new Blob([JSON.stringify(extract)], { type: 'application/json' });
		this.downloadData(blob, 'table-data.json');
	}

	generateCSV(event) {
		const extract = [];
		let extractString;
		const headers = this.columns.map(el => {
			return `"${el.header.split('"').join('""')}"`;
		});
		extract.push(headers.join(','));
		const rows = this.filteredRows.map((row, index) => {
			const tempRow = [];
			row.cells.forEach(val => {
				tempRow.push(
					`"${String(val)
						.split('"')
						.join('""')}"`
				);
			});
			const formattedRow = tempRow.join(',');
			extract.push(formattedRow);
			return formattedRow;
		});
		extractString = extract.join('\n');
		const blob = new Blob([extractString], { type: 'text/csv' });
		this.downloadData(blob, 'table-data.csv');
	}

	downloadData(blob: Blob, filename: string) {
		const element = document.createElement('a');
		if (blob) {
			if (navigator.msSaveOrOpenBlob) {
				navigator.msSaveOrOpenBlob(blob, filename);
			} else {
				element.setAttribute('href', window.URL.createObjectURL(blob));

				// set file title
				element.setAttribute('download', filename);

				// trigger download
				element.style.display = 'none';
				document.body.appendChild(element);
				element.click();

				// remove temporary link element
				document.body.removeChild(element);
			}
		}
	}

	overwriteClipboardData(e: ClipboardEvent) {
		let textSelected = false;
		if (typeof window.getSelection !== 'undefined') {
			textSelected = window.getSelection().toString() !== '';
		}
		if (!textSelected) {
			e.preventDefault();
			// NOTE: removed browser incompatible code from Vue counterpart

			e.clipboardData.setData('text/plain', this.generateCopy());
			this.copyFeedbackActive = true;
		}
	}

	resetFilteredRows() {
		this.filteredRows = (this.rows || []).slice();
	}

	/* sub-component related */
	advancedSearchObjectUpdateHandler(advancedSearchEvent: IDatatableAdvancedSearchEvent) {
		if (
			this.advancedSearchObjects.filter(o => o.column === advancedSearchEvent.column).length === 0 &&
			advancedSearchEvent.value !== ''
		) {
			this.advancedSearchObjects.push(advancedSearchEvent);
		} else {
			if (advancedSearchEvent.value !== '') {
				this.advancedSearchObjects.map(o => {
					if (o.column === advancedSearchEvent.column) {
						o.value = advancedSearchEvent.value;
					}
				});
			} else {
				this.advancedSearchObjects = this.advancedSearchObjects.filter(o => {
					return o.column !== advancedSearchEvent.column;
				});
			}
		}

		//  reset the globalSearchTerm
		if (this.globalSearcherComponent && this.globalSearcherComponent.globalSearchTerm !== '') {
			this.globalSearcherComponent.reset();
		}

		// Reset current page to first page
		if (this.paginatorComponent) {
			this.paginatorComponent.onFirstPageButtonClicked();
		}

		this.filterRowsByAdvancedSearch();

		if (this.sortable && this.sortedColumn && this.sortedColumnIndex && this.sortType) {
			this.sortFilteredRows(this.sortedColumn, this.sortedColumnIndex, this.sortType);
		}
	}

	filterRowsByAdvancedSearch() {
		this.filteredRows = this.rows.filter(r => {
			return this.advancedSearchObjects.every(sObject => {
				// each search object contains an index which specifies exactly what column to look at
				const cell = sObject.columnIndex ? r.cells[sObject.columnIndex] : null;
				if (cell) {
					return typeof sObject.column.sortField !== 'undefined'
						? cell.value
								.toString()
								.toLowerCase()
								.indexOf(sObject.value.toLowerCase()) > -1
						: cell
								.toString()
								.toLowerCase()
								.indexOf(sObject.value.toLowerCase()) > -1;
				} else {
					return false;
				}
			});
		});

		this.currentPage = 1;
	}

	globalSearchFocusoutHandler(globalSearchEvent: IDatatableGlobalSearchEvent) {
		// update global search term
		this.globalSearchTerm = globalSearchEvent.value;
		this.onGlobalSearchFocusout.emit(globalSearchEvent);
	}

	globalSearchTermUpdateHandler(newTermEvent: IDatatableGlobalSearchEvent) {
		// update global search term
		this.globalSearchTerm = newTermEvent.value;
		// filter rows by global search term
		this.filterRowsByGlobalSearch();
		// emit the event of onGlobalSearchFocusOut, which happens when the global search term changes
		this.onGlobalSearchTermUpdate.emit(newTermEvent);

		// reset current page to first page
		if (this.paginatorComponent) {
			this.paginatorComponent.onFirstPageButtonClicked();
		}

		// reset advanced searchers
		if (this.advancedSearcherComponents.length) {
			this.advancedSearcherComponents.forEach(asComp => {
				asComp.reset();
			});
		}
		this.advancedSearchObjects = [];
	}

	filterRowsByGlobalSearch() {
		this.filteredRows = this.rows.filter(r => {
			let hasTerm = false;
			r.cells
				.filter((cell, cellIndex) => {
					const currentColumn = this.getColumnByColumnIndex(cellIndex);
					return typeof currentColumn.searchable === 'undefined' || currentColumn.searchable;
				})
				.forEach((cell, cellIndex) => {
					// Only filter table based on displayed columns
					const currentColumn = this.getColumnByColumnIndex(cellIndex);

					if (
						this.datatableService
							.assertCellType(cell)
							.toString()
							.toLowerCase()
							.indexOf(this.globalSearchTerm.toLowerCase()) > -1
					) {
						hasTerm = true;
						return;
					}
				});
			return hasTerm;
		});

		if (this.pagination) {
			this.currentPage = 1;
		}
		if (this.selectableRows) {
			this.selectAll = false;
		}
		if (this.sortable && this.sortedColumn && this.sortedColumnIndex && this.sortType) {
			this.sortFilteredRows(this.sortedColumn, this.sortedColumnIndex, this.sortType);
		}
	}

	paginatorButtonClickHandler(paginatorEvent: IDatatablePaginatorEvent) {
		this.onPaginatorButtonClicked.emit(paginatorEvent);
		this.currentPage = paginatorEvent.pageNumber;
		// Set checkbox status if selectableRows
		if (this.selectableRows) {
			// Reset selectedRows
			this.selectedRows = [];
			// Reset checkbox status
			this.selectAll = false;
		}
	}

	pageSizeSelectChangeHandler(pageSizeEvent: IDatatablePageSizeEvent) {
		this.currentPage = pageSizeEvent.currentPage;
		this.currentPageSize =
			typeof pageSizeEvent.pageSize === 'string' ? parseInt(pageSizeEvent.pageSize, 10) : pageSizeEvent.pageSize;
		this.resetFilteredRows();

		// redo the global search if applied
		if (this.isGlobalSearchable && this.globalSearchTerm) {
			this.filterRowsByGlobalSearch();
		}

		// redo the sort if applied
		if (this.sortable && this.sortedColumn && this.sortedColumnIndex && this.sortType) {
			this.sortFilteredRows(this.sortedColumn, this.sortedColumnIndex, this.sortType);
		}

		// Set checkbox status if selectableRows
		if (this.selectableRows) {
			// Reset selectedRows
			this.selectedRows = [];

			// Reset checkbox status
			this.selectAll = false;
			this.displayedRowsOnPage.forEach(row => (row.checked = false));
		}

		this.onPageSizeSelectChanged.emit(pageSizeEvent);
	}

	columnFilterCheckboxUpdateHandler(selectedColumns: Column[]) {
		// reset the globalsearchterm
		if (this.globalSearcherComponent) {
			this.globalSearcherComponent.reset();
		}

		this.columns = this.columns.map(existingCol => {
			const matchCol = selectedColumns.find(selectedCol => {
				return selectedCol.header === existingCol.header;
			});
			if (!!matchCol) {
				existingCol.show = true;
				return existingCol;
			} else {
				existingCol.show = false;
				return existingCol;
			}
		});

		this.onColumnsChanged.emit(selectedColumns);
	}

	/* view utilities */
	trackColumns(index: number, col: Column) {
		return `${index}${col.header}`;
	}
	trackColspanColumns(index: number, colspan: number) {
		return `${index}${colspan}`;
	}
	trackRows(index: number, row: Row) {
		return row.id;
	}
	onWindowSizeChanged() {
		//  Reset columns when windows size changed
		const windowWidth = document.documentElement.getBoundingClientRect().width;
		if (windowWidth >= 1200) {
			// Reset column visibility by screen size
			this.columns.forEach(c => {
				c.show = c.showForLargeScreen;
			});
		}

		if (windowWidth >= 568 && windowWidth < 1200) {
			// Reset column visibility by screen size
			this.columns.forEach(c => {
				c.show = c.showForMediumScreen;
			});
		}

		if (windowWidth < 568) {
			// Reset column visibility by screen size
			this.columns.forEach(c => {
				c.show = c.showForSmallScreen;
			});
		}
	}
}
